import { Box, Button, Link, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import OtpInput from 'react-otp-input';
import { useLocation, useNavigate } from 'react-router-dom';
import { CAIRNS_LOGO_IMAGE_URL } from 'assets/images/images';
import BackWithTitle from 'components/BackWithTitle/BackWithTitle';
import { PaneBottomContactPanel } from 'components/PaneBottomContactPanel/PaneBottomContactPanel';
import { Text } from 'components/Typography';
import { Color } from 'utils/color';
import { RouteNames } from 'utils/navigationUtils';
import { getQueryParamObject } from 'utils/util';
import { SUPPORT_EMAIL } from '../../../../../../configConsts';
import style from './PhoneVerificationPaneV2.scss';

interface Props {
  acceptInvite: Function;
  requestSmsVerification: Function;
  handleError: (err: any) => void;
  loadingState: [boolean, Dispatch<SetStateAction<boolean>>];
  handleExternalRouting: (url?: Nullable<string>) => void;
}

export const PhoneVerificationPaneV2: React.FC<Props> = ({ acceptInvite, requestSmsVerification, handleError, loadingState, handleExternalRouting }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const token: string = getQueryParamObject(location?.search)?.token;
  const phoneNumber: string = getQueryParamObject(location?.search)?.phoneNumber || '';

  const [code, setCode] = useState<string>('');
  const [phoneVerificationError, setPhoneVerificationError] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(60); // 60 seconds countdown
  const [canResend, setCanResend] = useState<boolean>(false);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Set loading to false on mount
  useEffect(() => {
    loadingState[1](false);
  }, []);

  const handleCodeChange = (value: string) => {
    setCode(value);
    if (phoneVerificationError) {
      setPhoneVerificationError('');
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setPhoneVerificationError('Incorrect code number');
      return;
    }

    try {
      loadingState[1](true);
      setPhoneVerificationError('');

      await acceptInvite(
        {
          phoneNumber: phoneNumber,
          smsVerificationCode: code,
          isNewSignupFlow: true
        },
        token
      );

      navigate(RouteNames.SUCCESS_AUTH);
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setPhoneVerificationError(error?.data?.message || 'Phone sign up verification failed. Please try again or use other method.');
    } finally {
      loadingState[1](false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    try {
      loadingState[1](true);
      setPhoneVerificationError('');

      await requestSmsVerification(token, phoneNumber);

      // Reset countdown
      setCountdown(60);
      setCanResend(false);
      setCode('');
    } catch (error: any) {
      console.error('Error resending code:', error);
      setPhoneVerificationError('Failed to resend code. Please try again.');
    } finally {
      loadingState[1](false);
    }
  };

  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // Format as +1 (XXX) XXX - XXXX
    if (digits.length >= 10) {
      const countryCode = digits.length === 11 ? digits[0] : '1';
      const areaCode = digits.slice(-10, -7);
      const prefix = digits.slice(-7, -4);
      const lineNumber = digits.slice(-4);
      return `+${countryCode} (${areaCode}) ${prefix} - ${lineNumber}`;
    }

    return phone; // Return as-is if can't format
  };

  const handleBackToSignup = () => {
    navigate(`${RouteNames.INVITE}?token=${token}&isNewSignupFlow=true`);
  };

  const isCodeComplete = code.length === 6;

  return (
    <React.Fragment>
      <Box className={style.paneContainer}>
        <Box className={style.paneContentWrapper}>
          <div className={style.mobileLogoWrapper}>
            <img src={CAIRNS_LOGO_IMAGE_URL} alt="Cairns Health" />
          </div>
          <div className={style.desktopBackButtonWrapper}>
            <BackWithTitle title="Back to sign up" textVariant="nlg" textColor={Color.DARK_BLUE_700} onClick={handleBackToSignup} />
          </div>
          <div className={style.textWrapper}>
            <Text variant="h5">Enter the 6-digit code</Text>
            <Text variant="nlg">
              Sent to you at{' '}
              <Text variant="nlg" weight={600}>
                {formatPhoneNumber(phoneNumber)}
              </Text>
            </Text>
          </div>

          {/* 6-digit code input using react-otp-input */}
          <Box className={`${style.otpInputWrapper} ${phoneVerificationError ? style.otpInputWrapperError : ''}`}>
            <OtpInput
              value={code}
              onChange={handleCodeChange}
              numInputs={6}
              renderSeparator={<span className={style.separator}></span>}
              renderInput={(props) => <input {...props} placeholder="—" />}
              inputStyle={style.codeInput}
              containerStyle={style.codeInputContainer}
              shouldAutoFocus={true}
              inputType="tel"
            />
          </Box>

          {/* Error message below input */}
          {phoneVerificationError && <Typography className={style.inputErrorText}>{phoneVerificationError}</Typography>}

          {/* Resend code text */}
          <Box className={style.resendContainer}>
            {canResend ? (
              <Typography className={style.resendLink} onClick={handleResendCode}>
                Resend code
              </Typography>
            ) : (
              <Typography className={style.resendText}>Resend code in {formatCountdown(countdown)}</Typography>
            )}
          </Box>

          {/* Verify Button */}
          <Button variant="contained" fullWidth className={style.verifyButton} onClick={handleVerifyCode} disabled={!isCodeComplete || loadingState[0]}>
            Verify
          </Button>
        </Box>
        {/* Footer */}
        <PaneBottomContactPanel email={SUPPORT_EMAIL} isMobileHidden />
      </Box>
    </React.Fragment>
  );
};
